import { Moon, Sun, Monitor } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { NodeAvatar } from '@/components/nodes/NodeAvatar'
import { MobileSlideInMenu } from '@/components/layout/MobileSlideInMenu'
import { cn } from '@/lib/utils/cn'
import { useState } from 'react'
import { User } from '@/types'

interface MobileUserMenuProps {
  user: User
  theme: string
  onThemeChange: (theme: string) => void
  onNavigate: (path: string) => void
  onLogout: () => void
  onClose: () => void
}

interface UserMenuProps {
  user: User
  theme: string
  onThemeChange: (theme: string) => void
  onLogout: () => Promise<void>
}

function MobileUserMenu({
  user,
  theme,
  onThemeChange,
  onNavigate,
  onLogout,
  onClose,
}: MobileUserMenuProps) {
  // User menu options
  const userMenuOptions = [
    {
      label: 'Profile',
      action: () => onNavigate(`/users/${user.id}`),
      icon: null,
    },
    {
      label: 'Edit Profile',
      action: () => onNavigate(`/users/${user.id}/edit`),
      icon: null,
    },
    {
      label: 'Light',
      action: () => onThemeChange('light'),
      icon: <Sun className="h-4 w-4" />,
      isSelected: theme === 'light',
      type: 'theme',
    },
    {
      label: 'Dark',
      action: () => onThemeChange('dark'),
      icon: <Moon className="h-4 w-4" />,
      isSelected: theme === 'dark',
      type: 'theme',
    },
    {
      label: 'System',
      action: () => onThemeChange('system'),
      icon: <Monitor className="h-4 w-4" />,
      isSelected: theme === 'system',
      type: 'theme',
    },
    {
      label: 'Log out',
      action: () => onLogout(),
      icon: null,
      type: 'danger',
    },
  ]

  return (
    <>
      {/* User Info Header */}
      <div className="px-4 py-3 border-b mb-2">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
            <NodeAvatar node={user} size="lg" />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium leading-none">{user.name}</p>
            <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
          </div>
        </div>
      </div>

      {/* User Menu Options */}
      <div className="space-y-1">
        {userMenuOptions.map((option, index) => (
          <button
            key={index}
            onClick={() => {
              option.action()
              if (option.type !== 'theme') onClose()
            }}
            className={cn(
              'flex items-center gap-4 w-full rounded-xl px-4 py-3 transition-colors active:scale-95',
              option.type === 'danger'
                ? 'text-destructive hover:bg-destructive/10'
                : 'text-foreground hover:bg-muted active:bg-muted'
            )}
          >
            {option.icon && (
              <div className="flex items-center justify-center h-8 w-8">{option.icon}</div>
            )}
            {!option.icon && option.type !== 'theme' && (
              <div className="h-8 w-8" /> // Spacer for alignment
            )}
            <span className="text-base flex-1 text-left">{option.label}</span>
            {option.type === 'theme' && option.isSelected && (
              <span className="text-xs text-muted-foreground">✓</span>
            )}
          </button>
        ))}
      </div>
    </>
  )
}

export function UserMenu({ user, theme, onThemeChange, onLogout }: UserMenuProps) {
  const navigate = useNavigate()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const getThemeIcon = (themeValue: string) => {
    if (themeValue === 'light') return <Sun className="h-4 w-4" />
    if (themeValue === 'dark') return <Moon className="h-4 w-4" />
    return <Monitor className="h-4 w-4" />
  }

  const handleNavigate = (path: string) => {
    navigate(path)
    setIsMobileMenuOpen(false)
  }

  const handleLogout = async () => {
    await onLogout()
    setIsMobileMenuOpen(false)
  }

  return (
    <>
      {/* Desktop User Menu */}
      <div className="hidden lg:block">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-10 w-10 rounded-full">
              <NodeAvatar node={user} size="lg" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{user.name}</p>
                <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleNavigate(`/users/${user.id}`)}>
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleNavigate(`/users/${user.id}/edit`)}>
              Edit Profile
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Theme</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => onThemeChange('light')}>
              <div className="flex items-center gap-2 w-full">
                {getThemeIcon('light')}
                <span>Light</span>
                {theme === 'light' && (
                  <span className="ml-auto text-xs text-muted-foreground">✓</span>
                )}
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onThemeChange('dark')}>
              <div className="flex items-center gap-2 w-full">
                {getThemeIcon('dark')}
                <span>Dark</span>
                {theme === 'dark' && (
                  <span className="ml-auto text-xs text-muted-foreground">✓</span>
                )}
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onThemeChange('system')}>
              <div className="flex items-center gap-2 w-full">
                {getThemeIcon('system')}
                <span>System</span>
                {theme === 'system' && (
                  <span className="ml-auto text-xs text-muted-foreground">✓</span>
                )}
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>Log out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Mobile User Menu */}
      <div className="lg:hidden">
        <Button
          variant="ghost"
          className="relative h-10 w-10 rounded-full"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <NodeAvatar node={user} size="lg" />
        </Button>

        <MobileSlideInMenu
          id="mobile-user-menu"
          heading="User Menu"
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          mode="top"
        >
          <MobileUserMenu
            user={user}
            theme={theme}
            onThemeChange={onThemeChange}
            onNavigate={handleNavigate}
            onLogout={handleLogout}
            onClose={() => setIsMobileMenuOpen(false)}
          />
        </MobileSlideInMenu>
      </div>
    </>
  )
}
